import React, { useCallback, useEffect, useState } from "react";
import "styles/legacy/CuratorMovieReview.css";
import {
  curatorAcceptAsset,
  curatorRejectAsset,
  fetchCuratorReviewQueue,
} from "../api/curatorReview.api";
import { getAssetById } from "shared/api/client/orvalHelpers";
import { getApiOrigin } from "shared/api/client/apiOrigin";
import CuratorReviewHlsVideo from "../components/ReviewHlsPlayer";

type QueueItem = {
  id: string;
  title: string;
  status: string;
  thumbFile: string;
};

function normStatus(s: string | undefined | null): string {
  return (s ?? "").replace(/\s+/g, "").toLowerCase();
}

function isPendingReviewStatus(status: string): boolean {
  const n = normStatus(status);
  return (
    n === "underreview" ||
    n === "under_review" ||
    n === "submitted" ||
    n === "pending" ||
    n === "inreview"
  );
}

function readId(row: Record<string, unknown>): string {
  const raw = row.id ?? row.ID;
  if (typeof raw === "number" && Number.isFinite(raw)) return String(raw);
  if (typeof raw === "string") return raw;
  return "";
}

function readStr(row: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return "";
}

function thumbFromSummary(row: Record<string, unknown>): string {
  const imageLib = row.imageLib ?? row.ImageLib;
  if (Array.isArray(imageLib) && imageLib.length > 0) {
    const first = imageLib[0] as Record<string, unknown>;
    const fn = readStr(first, "fileName", "FileName");
    if (fn) return fn;
  }
  return readStr(row, "thumbnail", "Thumbnail", "thumbnailFileName");
}

function toQueueItems(raw: unknown[]): QueueItem[] {
  return raw
    .map((item) => {
      const row = item as Record<string, unknown>;
      const id = readId(row);
      if (!id) return null;
      return {
        id,
        title: readStr(row, "title", "Title") || "Untitled",
        status: readStr(row, "status", "Status") || "—",
        thumbFile: thumbFromSummary(row),
      };
    })
    .filter(Boolean) as QueueItem[];
}

function imageStreamUrl(fileName: string): string {
  if (!fileName) return "";
  return `${getApiOrigin()}/api/Image/stream/${encodeURIComponent(fileName)}`;
}

function pickVideo(
  asset: Record<string, unknown>,
  roleMatch: string
): Record<string, unknown> | null {
  const roleLower = roleMatch.toLowerCase();
  const videos = asset.videos ?? asset.Videos ?? asset.videoLib ?? asset.VideoLib;
  if (!Array.isArray(videos)) return null;
  const found = videos.find((v) => {
    const r = readStr(v as Record<string, unknown>, "role", "Role").toLowerCase();
    return r === roleLower;
  });
  return (found as Record<string, unknown>) ?? null;
}

function pickImage(
  asset: Record<string, unknown>,
  roleMatch: string
): Record<string, unknown> | null {
  const roleLower = roleMatch.toLowerCase();
  const images = asset.images ?? asset.Images ?? asset.imageLib ?? asset.ImageLib;
  if (!Array.isArray(images)) return null;
  const found = images.find((i) => {
    const r = readStr(i as Record<string, unknown>, "role", "Role").toLowerCase();
    return r === roleLower;
  });
  return (found as Record<string, unknown>) ?? null;
}

function readVideoId(v: Record<string, unknown> | null): number {
  if (!v) return 0;
  const raw = v.videoId ?? v.VideoId ?? v.id ?? v.ID;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string") {
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function readImageFileName(img: Record<string, unknown> | null): string {
  if (!img) return "";
  return readStr(img, "fileName", "FileName");
}

function subtitleRows(asset: Record<string, unknown>): { file: string; lang: string }[] {
  const subs =
    asset.subtitles ??
    asset.Subtitles ??
    asset.subtitleLib ??
    asset.SubtitleLib;
  if (!Array.isArray(subs)) return [];
  return subs.map((s) => {
    const r = s as Record<string, unknown>;
    return {
      file: readStr(r, "fileName", "FileName"),
      lang: readStr(r, "language", "Language"),
    };
  });
}

type MetaKey =
  | "title"
  | "language"
  | "category"
  | "genre"
  | "format"
  | "ageRating"
  | "comfortLevel"
  | "copyrightOwner"
  | "publisher"
  | "releaseDate"
  | "festivalParticipations"
  | "awards"
  | "status"
  | "price";

const META_FIELDS: { key: MetaKey; label: string }[] = [
  { key: "title", label: "Title" },
  { key: "language", label: "Language" },
  { key: "category", label: "Category" },
  { key: "genre", label: "Genre" },
  { key: "format", label: "Format" },
  { key: "ageRating", label: "Age rating" },
  { key: "comfortLevel", label: "Comfort level" },
  { key: "copyrightOwner", label: "Copyright owner" },
  { key: "publisher", label: "Publisher" },
  { key: "releaseDate", label: "Release date" },
  { key: "festivalParticipations", label: "Festival participations" },
  { key: "awards", label: "Awards" },
  { key: "status", label: "Status" },
  { key: "price", label: "Price" },
];

function readAssetStatus(asset: Record<string, unknown>): string {
  for (const key of [
    "status",
    "Status",
    "assetStatus",
    "AssetStatus",
    "state",
    "State",
  ]) {
    const v = asset[key];
    if (typeof v === "string" && v.trim() !== "") return v;
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
  }
  return "—";
}

function readMeta(asset: Record<string, unknown>, snake: MetaKey | "description"): string {
  if (snake === "status") {
    return readAssetStatus(asset).replace(/([a-z])([A-Z])/g, "$1 $2");
  }
  const pascal = snake.charAt(0).toUpperCase() + snake.slice(1);
  const raw =
    asset[snake] ??
    asset[pascal] ??
    (snake === "releaseDate" ? asset.ReleaseDate : undefined);
  if (raw === undefined || raw === null) return "—";
  if (typeof raw === "number") {
    if (snake === "price") return raw > 0 ? `€${raw.toFixed(2)}` : "Free";
    return String(raw);
  }
  if (typeof raw === "object") return JSON.stringify(raw);
  const s = String(raw).trim();
  return s || "—";
}

function nextSelectionAfterMutation(
  items: QueueItem[],
  mutatedId: string,
  detailStatus: string
): string | null {
  const inQueue = items.some((i) => i.id === mutatedId);
  if (!inQueue) {
    return items[0]?.id ?? null;
  }

  const row = items.find((i) => i.id === mutatedId);
  const queueSaysPending = row
    ? isPendingReviewStatus(row.status) || row.status === "—"
    : false;
  const detailSaysPending =
    detailStatus === "—" || isPendingReviewStatus(detailStatus);

  if (detailSaysPending && queueSaysPending) {
    return mutatedId;
  }

  const otherPending = items.find(
    (i) => i.id !== mutatedId && isPendingReviewStatus(i.status)
  );
  if (otherPending) {
    return otherPending.id;
  }

  const other = items.find((i) => i.id !== mutatedId);
  return other?.id ?? null;
}

function summarizeAcceptResponse(body: unknown, usedEncodeFallback: boolean): string {
  if (usedEncodeFallback) {
    const rec =
      body && typeof body === "object" ? (body as Record<string, unknown>) : null;
    const msg = rec && typeof rec.message === "string" ? rec.message : "";
    return (
      msg ||
      "Encoding has started. This title will become Published when processing finishes."
    );
  }
  const rec = body && typeof body === "object" ? (body as Record<string, unknown>) : null;
  const st = rec && typeof rec.status === "string" ? rec.status : "";
  if (st.toLowerCase() === "published") {
    return "Published successfully. This title is now in the catalog.";
  }
  const msg = rec && typeof rec.message === "string" ? rec.message : "";
  if (msg && /published/i.test(msg)) {
    return `Published successfully. ${msg}`;
  }
  return "Published successfully. This title is on its way to the catalog.";
}

function summarizeRejectResponse(body: unknown): string {
  if (body == null || body === "") {
    return "Rejected successfully. The creator will see the updated status in their library.";
  }
  const rec = body && typeof body === "object" ? (body as Record<string, unknown>) : null;
  const st = rec && typeof rec.status === "string" ? rec.status : "";
  const reason =
    (rec && typeof rec.reason === "string" ? rec.reason : "") ||
    (rec && typeof rec.rejectionReason === "string" ? rec.rejectionReason : "");
  if (st.toLowerCase() === "rejected" || reason) {
    return reason
      ? `Rejected successfully. Reason recorded: ${reason}`
      : "Rejected successfully. The creator will see the updated status in their library.";
  }
  return "Rejected successfully.";
}

const CuratorMovieReview: React.FC = () => {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [listError, setListError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [banner, setBanner] = useState<{ kind: "ok" | "err"; text: string } | null>(
    null
  );
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectSubmitError, setRejectSubmitError] = useState("");

  const refreshQueueAndReselect = useCallback(async (mutatedId: string) => {
    const rows = await fetchCuratorReviewQueue();
    const items = toQueueItems(rows).reverse();
    setQueue(items);

    let detailStatus = "—";
    let nextDetail: Record<string, unknown> | null = null;
    try {
      const data = await getAssetById(mutatedId);
      nextDetail =
        data && typeof data === "object" ? (data as Record<string, unknown>) : null;
      if (nextDetail) {
        detailStatus = readAssetStatus(nextDetail);
      }
    } catch {
      nextDetail = null;
    }

    const nextId = nextSelectionAfterMutation(items, mutatedId, detailStatus);

    if (nextId == null) {
      setSelectedId(null);
      setDetail(null);
      return;
    }

    if (nextId === mutatedId && nextDetail) {
      setSelectedId(mutatedId);
      setDetail(nextDetail);
      return;
    }

    setDetail(null);
    setSelectedId(nextId);
  }, []);

  const loadQueue = useCallback(async () => {
    setLoadingList(true);
    setListError("");
    try {
      const rows = await fetchCuratorReviewQueue();
      const items = toQueueItems(rows).reverse();
      setQueue(items);
      setSelectedId((prev) => {
        if (prev && items.some((i) => i.id === prev)) return prev;
        return items[0]?.id ?? null;
      });
    } catch (e) {
      setListError(e instanceof Error ? e.message : "Failed to load review queue.");
      setQueue([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoadingDetail(true);
    setDetailError("");
    getAssetById(selectedId)
      .then((data) => {
        if (!cancelled) {
          setDetail(
            data && typeof data === "object" ? (data as Record<string, unknown>) : null
          );
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setDetailError(e instanceof Error ? e.message : "Failed to load details.");
          setDetail(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const asset = detail;
  const mainVideo = asset ? pickVideo(asset, "MainVideo") : null;
  const trailerVideo = asset ? pickVideo(asset, "Trailer") : null;
  const heroImage = asset ? pickImage(asset, "HeroImage") : null;
  const thumbImage = asset ? pickImage(asset, "Thumbnail") : null;
  const mainVideoId = readVideoId(mainVideo);
  const trailerVideoId = readVideoId(trailerVideo);
  const heroFn = readImageFileName(heroImage);
  const thumbFn = readImageFileName(thumbImage);

  const detailStatus = asset ? readAssetStatus(asset) : "—";
  const selectedQueueItem = selectedId
    ? queue.find((q) => q.id === selectedId)
    : undefined;
  /**
   * Details DTO often omits `status` even when the title is UnderReview.
   * The review queue only lists under-review titles, so membership there
   * must still enable Accept / Reject when detail status is missing ("—").
   */
  const canDecide = Boolean(
    asset &&
      !loadingDetail && String(asset.id ?? asset.ID) === selectedId &&
      selectedQueueItem &&
      (isPendingReviewStatus(detailStatus) ||
        isPendingReviewStatus(selectedQueueItem.status) ||
        detailStatus === "—")
  );

  const runPublish = async () => {
    if (!selectedId || !asset || !canDecide || actionBusy) return;
    const idStr = selectedId;
    const idNum = Number.parseInt(idStr, 10);
    if (!Number.isFinite(idNum)) return;
    setActionBusy(true);
    setBanner(null);
    try {
      const { body, usedEncodeFallback } = await curatorAcceptAsset(idNum);
      setBanner({
        kind: "ok",
        text: summarizeAcceptResponse(body, usedEncodeFallback),
      });
      await refreshQueueAndReselect(idStr);
    } catch (e) {
      setBanner({
        kind: "err",
        text: e instanceof Error ? e.message : "Could not accept title.",
      });
    } finally {
      setActionBusy(false);
    }
  };

  const runReject = async () => {
    if (!selectedId || !asset || !canDecide || actionBusy) return;
    const idStr = selectedId;
    const idNum = Number.parseInt(idStr, 10);
    if (!Number.isFinite(idNum)) return;
    setActionBusy(true);
    setBanner(null);
    try {
      const body = await curatorRejectAsset(idNum, rejectReason);
      setRejectOpen(false);
      setRejectReason("");
      setRejectSubmitError("");
      setBanner({
        kind: "ok",
        text: summarizeRejectResponse(body),
      });
      await refreshQueueAndReselect(idStr);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not reject title.";
      setRejectSubmitError(msg);
      setBanner({
        kind: "err",
        text: msg,
      });
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className="library-creator-content curator-movie-review">
      <div className="main-library">
        <div className="top-container-library">
          <div className="left-top-container-library">
            <div className="title-top-container-library">Movie review</div>
            <div className="subtitle-top-container-library curator-review-subtitle">
              Inspect submitted titles: full metadata, images, and encoded streams. Publish
              titles to the catalog or reject them.
            </div>
          </div>
        </div>

        {banner && (
          <div
            className={`curator-review-banner-row curator-review-message curator-review-message--${
              banner.kind === "ok" ? "ok" : "err"
            }`}
            role="status"
          >
            <span className="curator-review-banner-text">{banner.text}</span>
            <button
              type="button"
              className="curator-review-banner-dismiss"
              onClick={() => setBanner(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        {listError && (
          <div className="curator-review-message curator-review-message--err">
            {listError}
          </div>
        )}

        <div className="curator-review-layout">
          <div className="curator-review-queue">
            <div className="curator-review-queue-header">
              <div className="curator-review-queue-title">Under review</div>
            </div>
            <div className="curator-review-queue-list">
              {loadingList ? (
                <div className="curator-review-empty">Loading…</div>
              ) : queue.length === 0 ? (
                <div className="curator-review-empty">No titles in the review queue.</div>
              ) : (
                queue.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className={`curator-review-queue-item ${
                      selectedId === item.id
                        ? "curator-review-queue-item--active"
                        : ""
                    }`}
                    onClick={() => setSelectedId(item.id)}
                  >
                    {item.thumbFile ? (
                      <img
                        className="curator-review-queue-thumb"
                        src={imageStreamUrl(item.thumbFile)}
                        alt=""
                      />
                    ) : (
                      <div className="curator-review-queue-thumb" />
                    )}
                    <div className="curator-review-queue-meta">
                      <div className="curator-review-queue-item-title">{item.title}</div>
                      <div className="curator-review-queue-status">{item.status.replace(/([a-z])([A-Z])/g, "$1 $2")}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="curator-review-detail">
            {loadingDetail && (
              <div className="curator-review-empty">Loading title…</div>
            )}
            {detailError && (
              <div className="curator-review-message curator-review-message--err">
                {detailError}
              </div>
            )}
            {!loadingDetail && !asset && !detailError && (
              <div className="curator-review-empty">Select a title from the queue.</div>
            )}
            {asset && (
              <>
                <div className="curator-review-detail-title">
                  {readMeta(asset, "title")}
                </div>

                <div className="curator-review-actions">
                  <button
                    type="button"
                    className="curator-review-btn curator-review-btn-publish"
                    disabled={!canDecide || actionBusy}
                    onClick={() => void runPublish()}
                  >
                    Accept / publish
                  </button>
                  <button
                    type="button"
                    className="curator-review-btn curator-review-btn-reject"
                    disabled={!canDecide || actionBusy}
                    onClick={() => {
                      setRejectReason("");
                      setRejectSubmitError("");
                      setRejectOpen(true);
                    }}
                  >
                    Reject
                  </button>
                </div>

                <div className="curator-review-meta-grid">
                  {META_FIELDS.map(({ key, label }) => (
                    <div className="curator-review-meta-field" key={key}>
                      <span className="curator-review-meta-label">{label}</span>
                      <span className="curator-review-meta-value">
                        {readMeta(asset, key)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="curator-review-meta-field">
                  <span className="curator-review-meta-label">Description</span>
                  <span className="curator-review-meta-value">
                    {readMeta(asset, "description")}
                  </span>
                </div>

                <div className="curator-review-media-grid">
                  {thumbFn ? (
                    <div>
                      <div className="curator-review-media-label">Thumbnail</div>
                      <img
                        className="curator-review-static-image"
                        src={imageStreamUrl(thumbFn)}
                        alt="Thumbnail"
                      />
                    </div>
                  ) : null}
                  {heroFn ? (
                    <div>
                      <div className="curator-review-media-label">Hero image</div>
                      <img
                        className="curator-review-static-image"
                        src={imageStreamUrl(heroFn)}
                        alt="Hero"
                      />
                    </div>
                  ) : null}
                </div>

                <div className="curator-review-media-grid">
                  {mainVideoId > 0 ? (
                    <CuratorReviewHlsVideo videoId={mainVideoId} title="Main video (HLS)" />
                  ) : null}
                  {trailerVideoId > 0 ? (
                    <CuratorReviewHlsVideo
                      videoId={trailerVideoId}
                      title="Trailer (HLS)"
                    />
                  ) : null}
                </div>

                {subtitleRows(asset).length > 0 ? (
                  <div className="curator-review-subtitles">
                    <div className="curator-review-media-label">Subtitles</div>
                    <ul>
                      {subtitleRows(asset).map((s) => (
                        <li key={`${s.file}-${s.lang}`}>
                          {s.file || "—"}
                          {s.lang ? ` (${s.lang})` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {rejectOpen ? (
        <div
          className="curator-review-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setRejectOpen(false);
              setRejectSubmitError("");
            }
          }}
        >
          <div
            className="curator-review-modal"
            role="dialog"
            aria-labelledby="curator-reject-title"
          >
            <h2 id="curator-reject-title" className="curator-review-modal-title">
              Reject title
            </h2>
            <p className="curator-review-modal-hint">
              A reason is required. It may be shown to the creator when the API supports it.
            </p>
            <textarea
              className="curator-review-modal-textarea"
              value={rejectReason}
              onChange={(e) => {
                setRejectReason(e.target.value);
                if (rejectSubmitError) setRejectSubmitError("");
              }}
              placeholder="e.g. Audio is out of sync with the video track."
              rows={4}
              disabled={actionBusy}
            />
            {rejectSubmitError ? (
              <div className="curator-review-modal-error">{rejectSubmitError}</div>
            ) : null}
            <div className="curator-review-modal-actions">
              <button
                type="button"
                className="curator-review-modal-btn curator-review-modal-btn-cancel"
                disabled={actionBusy}
                onClick={() => {
                  setRejectOpen(false);
                  setRejectSubmitError("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="curator-review-btn curator-review-btn-reject"
                disabled={actionBusy || !canDecide || !rejectReason.trim()}
                onClick={() => void runReject()}
              >
                Submit rejection
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CuratorMovieReview;
